// src/components/DetalleZonaEconomicaEspecial.js
//
// Modal de detalle para Zonas Económicas Especiales (tab 2, sublayer 1).
// Mismo patrón visual que DetalleParqueIndustrial (gallery + secciones KV).
// Reemplaza al inline popup detallado `loadZonaEconomicaEspecialDetalle`.

import React, { useEffect, useState } from "react";
import parquesIndustriales from "../services/parquesService";
import { AppConfig } from "../config/appConfig";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibraryOutlined";
import FullscreenIcon from "@mui/icons-material/FullscreenRounded";
import ImageModal from "./ImageModal";
import "./DetalleParqueIndustrial.css";
import "./popup-styles.css";

// Mapa hardcodeado de fotos por id_intervencion — mismas URLs que ya tenía
// el inline popup legacy (`loadZonaEconomicaEspecialDetalle`). Lo dejamos
// como fallback porque `listarImagenesParque` no aplica a ZEDs en backend.
const _m = () => AppConfig.MEDIA_BASE_URL;
const FALLBACK_ZED_IMAGES_BY_INTERVENCION = {
  427: () => [`${_m()}/zonas_img/ZED_PAITA.jpg`],
  425: () => [`${_m()}/zonas_img/ZOFRATACNA.jpg`],
  424: () => [`${_m()}/zonas_img/ZED_ILO.jpg`],
  828: () => [`${_m()}/zonas_img/ZED_MATARANI.png`],
};

// Lookup secundario por NOMBRE — más estable entre ambientes si las IDs
// cambian (mismo razonamiento que DetalleParqueIndustrial).
const FALLBACK_ZED_IMAGES_BY_NAME = {
  "ZED PAITA": FALLBACK_ZED_IMAGES_BY_INTERVENCION[427],
  "PAITA": FALLBACK_ZED_IMAGES_BY_INTERVENCION[427],
  "ZED ZOFRATACNA": FALLBACK_ZED_IMAGES_BY_INTERVENCION[425],
  "ZOFRATACNA": FALLBACK_ZED_IMAGES_BY_INTERVENCION[425],
  "ZED ILO": FALLBACK_ZED_IMAGES_BY_INTERVENCION[424],
  "ILO": FALLBACK_ZED_IMAGES_BY_INTERVENCION[424],
  "ZED MATARANI": FALLBACK_ZED_IMAGES_BY_INTERVENCION[828],
  "MATARANI": FALLBACK_ZED_IMAGES_BY_INTERVENCION[828],
};

const _resolveFallbackImages = (idIntervencion, ...nameCandidates) => {
  const _resolve = (val) => (typeof val === "function" ? val() : val);
  const byId =
    _resolve(FALLBACK_ZED_IMAGES_BY_INTERVENCION[idIntervencion]) ||
    _resolve(FALLBACK_ZED_IMAGES_BY_INTERVENCION[String(idIntervencion)]) ||
    _resolve(FALLBACK_ZED_IMAGES_BY_INTERVENCION[Number(idIntervencion)]);
  if (byId && byId.length > 0) return byId;
  for (const candidate of nameCandidates) {
    if (!candidate) continue;
    const key = String(candidate).trim().toUpperCase();
    if (!key) continue;
    if (FALLBACK_ZED_IMAGES_BY_NAME[key]) return _resolve(FALLBACK_ZED_IMAGES_BY_NAME[key]);
    for (const knownName of Object.keys(FALLBACK_ZED_IMAGES_BY_NAME)) {
      if (key.includes(knownName)) return _resolve(FALLBACK_ZED_IMAGES_BY_NAME[knownName]);
    }
  }
  return [];
};

// id_infraestructura → campo del DTO de getIntervencionParamDetalle. Mismos
// IDs que usaba el inline popup legacy de ZEDs.
const FIELD_IDS = {
  hectareas: 32,
  habilitar: 33,
  empresasInstaladas: 34,
  principalesEmpresas: 35,
  actividadesPermitidas: 36,
  productosExporta: 37,
  departamento: 38,
  provincia: 39,
  distrito: 40,
  direccion: 41,
};

const Gallery = ({ images, loading, altPrefix = "Imagen" }) => {
  const [index, setIndex] = useState(0);
  const [fullscreenSrc, setFullscreenSrc] = useState(null);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  if (loading) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <div className="pdet-gallery-spinner" />
        <span>Cargando imágenes…</span>
      </div>
    );
  }
  if (!images || images.length === 0) {
    return (
      <div className="pdet-gallery pdet-gallery-empty">
        <PhotoLibraryIcon style={{ fontSize: 32, opacity: 0.55 }} />
        <span>Sin imágenes disponibles</span>
      </div>
    );
  }

  const prev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const next = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };
  const openFullscreen = (e) => {
    e?.stopPropagation?.();
    setFullscreenSrc(images[index]);
  };

  return (
    <>
      <div className="pdet-gallery">
        <img
          className="pdet-gallery-img pdet-gallery-img--clickable"
          src={images[index]}
          alt={`${altPrefix} ${index + 1}`}
          onClick={openFullscreen}
          title="Ver en pantalla completa"
        />
        <button
          type="button"
          className="pdet-gallery-fullscreen"
          onClick={openFullscreen}
          aria-label="Ver en pantalla completa"
          title="Ver en pantalla completa"
        >
          <FullscreenIcon style={{ fontSize: 20 }} />
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="pdet-gallery-nav left"
              onClick={prev}
              aria-label="Imagen anterior"
            >
              <ChevronLeftIcon style={{ fontSize: 28 }} />
            </button>
            <button
              type="button"
              className="pdet-gallery-nav right"
              onClick={next}
              aria-label="Siguiente imagen"
            >
              <ChevronRightIcon style={{ fontSize: 28 }} />
            </button>
            <div className="pdet-gallery-counter">
              {index + 1} / {images.length}
            </div>
          </>
        )}
      </div>
      <ImageModal
        open={!!fullscreenSrc}
        onClose={() => setFullscreenSrc(null)}
        imageUrl={fullscreenSrc || ""}
        altText={`${altPrefix} ${index + 1} de ${images.length}`}
      />
    </>
  );
};

const KV = ({ label, value }) => {
  if (!value || value === "—" || value === "-" || String(value).trim() === "")
    return null;
  return (
    <div className="parq-popup-kv">
      <span className="parq-popup-kv-label">{label}</span>
      <span className="parq-popup-kv-value">{value}</span>
    </div>
  );
};

const Section = ({ title, children }) => {
  const hasContent = React.Children.toArray(children).some((c) => c);
  if (!hasContent) return null;
  return (
    <div className="parq-popup-section">
      <h5 className="parq-popup-section-title">{title}</h5>
      <div className="parq-popup-grid">{children}</div>
    </div>
  );
};

const DetalleZonaEconomicaEspecial = ({ idIntervencion, nombre, onClose }) => {
  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idIntervencion) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    setImages([]);

    parquesIndustriales
      .getIntervencionParamDetalle(idIntervencion)
      .catch(() => null)
      .then((response) => {
        if (cancelled) return;
        const item = response?.item;
        const params = item?.lista_param || [];
        const getValue = (idInfra) => {
          const row = params.find((x) => x.id_infraestructura === idInfra);
          return (row?.valor_descripcion || "").trim();
        };
        const fields = Object.fromEntries(
          Object.entries(FIELD_IDS).map(([key, id]) => [key, getValue(id)])
        );
        setData({
          ...fields,
          nombre_proyecto:
            item?.nombre_proyecto || nombre || "Zona Económica Especial",
        });
        // ZEDs no usan listarImagenesParque (no aplica al backend); siempre
        // caemos al fallback hardcodeado por id o por nombre.
        setImages(
          _resolveFallbackImages(
            idIntervencion,
            item?.nombre_proyecto,
            nombre
          )
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idIntervencion, nombre]);

  return (
    <div className="pdet-card">
      <button
        type="button"
        className="pdet-close"
        onClick={onClose}
        aria-label="Cerrar"
      >
        <CloseIcon style={{ fontSize: 20 }} />
      </button>

      <div className="pdet-card-head">
        <span className="pdet-pill">Zona Económica Especial</span>
        <h2 className="pdet-title">
          {data?.nombre_proyecto || nombre || "Zona Económica Especial"}
        </h2>
      </div>

      <div className="pdet-card-body">
        <Gallery
          images={images}
          loading={loading}
          altPrefix={`ZED ${data?.nombre_proyecto || nombre || ""} —`}
        />

        {loading ? (
          <div className="pdet-loading">Cargando información…</div>
        ) : (
          data && (
            <>
              <Section title="Ubicación">
                <KV label="Departamento" value={data.departamento} />
                <KV label="Provincia" value={data.provincia} />
                <KV label="Distrito" value={data.distrito} />
                <KV label="Dirección" value={data.direccion} />
              </Section>

              <Section title="Hectáreas">
                <KV label="Totales (ha)" value={data.hectareas} />
                <KV label="A habilitar (ha)" value={data.habilitar} />
              </Section>

              <Section title="Empresas y actividad">
                <KV
                  label="Empresas instaladas"
                  value={data.empresasInstaladas}
                />
                <KV
                  label="Principales empresas"
                  value={data.principalesEmpresas}
                />
                <KV
                  label="Actividades permitidas"
                  value={data.actividadesPermitidas}
                />
                <KV
                  label="Productos que exporta"
                  value={data.productosExporta}
                />
              </Section>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default DetalleZonaEconomicaEspecial;
